// src/com/trans_plan_return/TransPlanReturnInput.js
import React, { useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/system';
import { styled } from '@mui/material/styles';
import { Button, Grid } from '@mui/material';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'; // issue TSR
import CloudUploadIcon from '@mui/icons-material/CloudUpload'; // save
import RepartitionIcon from '@mui/icons-material/Repartition'; // 집기 반납 확인
import AssignmentLateIcon from '@mui/icons-material/AssignmentLate'; // 반납정보 미입력
import ArrowBackIcon from '@mui/icons-material/ArrowBack'; // close
import { ContentHeader, ControlBox, ToolBox } from '../../common/TopViewControl';
import { ApiMsg, AppMenuUrl, AppObject, AppStyle, AppWord } from '../../value/AppConst';
import {
    authTableTreeRepo, dcInfoListRepo, menuRequestRepo, menuTreeRepo, transPlanTsrListParamRepo, userInfoRepo
} from '../../value/AppModel';
import { useMutation, useReactiveVar } from '@apollo/client';
import { useConfirmDialog } from '../../common/AppConfirmDialog';
import { useAlertSetter, useAuthorizationCheck } from '../../api/app-hook';
import VzmTextField from '../../common/VzmTextField';
import InputUtil from '../../value/InputUtil';
import { TransPlanReturnDictionary } from './TransPlanReturnDict';
import DurationPicker from '../../common/DurationPicker';
import ValueMisc from '../../value/ValueMisc';
import VzmIntField from '../../common/VzmIntField';
import {
    SAVE_TRANS_PLAN_RETURN, SET_TRANS_PLAN_RETURN_DC_CHECK, SET_TRANS_PLAN_RETURN_NO_RETURN
} from './TransPlanReturnGql';
import { enqueueSnackbar } from 'notistack';
import TransPlanReturnNoReturnInput from './TransPlanReturnNoReturnInput';
import { TAKE_AT_STORE_TRANS_PLAN_SEAL_CHECK_INFO } from '../trans_plan_seal_check_info/TransPlanSealCheckInfoGql';

// 일반적인 Dialog를 사용한 입력화면이 아님.
// 점포관리 화면에서 점포목록에서 선택한 점포의 반품정보를 보여주는 화면으로 아래에 표시됨.
// 닫기 버튼으로 닫을 수 있음.

const EditorBox = styled(Box)({
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    // height: '100%',
    width: '100%',
    border: AppStyle.BorderCCC, borderRadius: 5, margin:'10px 5px 2px 5px', boxSizing: 'border-box',
});

const EditorBody = styled(Box)({
    flexGrow: 1, overflow: 'auto', padding: '10px',
});

const GroupLabel = styled(Box)({
    fontSize: '0.82rem', justifyContent: 'end', height: '100%', display: 'flex', alignItems: 'center',
    color: '#555'
});
const InputGroupBox = styled(Box)({
    flexGrow: 1, display: 'flex', alignItems: 'center', //borderTop: AppStyle.BorderCCC
});
const InputHolder = styled(Box)({
    display: 'inline-flex', alignItems: 'center', padding: '0px 2px 0px 2px'
});
const LabelOverInputBox = styled(Box)({
    borderRadius: 5, fontSize: '0.85rem', backgroundColor: '#eee',
});
const InputLabelBox = styled(Box)({
    height: 40,
    display: 'flex', justifyContent: 'center', alignItems: 'center',
});
const PairLeft = styled(Box)({display:'flex', alignItems:'center', justifyContent:'center', width: '50%', padding: 2});
const PairRight = styled(Box)({display:'flex', alignItems:'center', justifyContent:'center', width: '50%', padding: 2, borderLeft: '1px solid #fff'});
const PairInput = styled(Box)({display:'flex', alignItems:'center', justifyContent:'center', backgroundColor: '#fff', paddingTop:10});
const SingleInput = styled(Box)({
    display:'flex', alignItems:'center', justifyContent:'center', paddingTop: 11, paddingBottom: 2,
    backgroundColor: '#fff', 
});

const InputMap = TransPlanReturnDictionary.inputMap;

/**
 * 물류집기 입력
 * 점포관리(ArrivalToStore.js)에서 사용하는 컴포넌트.
 * 점포의 반품정보를 보여주는 화면
 * DC 사용자와 점포 사용자의 동작이 약간 다름(TSR 발행 이동).
 * @param {*} param0 
 * @returns 
 */
export default function TransPlanReturnInput({
    open,
    onClose,
    item, // item of arrival_to_store OR trans_plan_return. (같음)
    onCompleteMutation,
}) {
    const sessionInfo = useReactiveVar(userInfoRepo);
    const authTableTree = useReactiveVar(authTableTreeRepo);
    const dcInfoListData = useReactiveVar(dcInfoListRepo);
    const [input, setInput] = useState({...AppObject.emptyInputData});
    const [isEdit, setIsEdit] = useState(false);
    const [dcAndStore, setDcAndStore] = useState({});
    const [openNoReturnInput, setOpenNoReturnInput] = useState(false);
    const menuTree = useReactiveVar(menuTreeRepo);
    const openConfirmDialog = useConfirmDialog();
    const addAlert = useAlertSetter();
    const checkAuth = useAuthorizationCheck();
    const dcTypeCd = useMemo(()=>{
        if(dcInfoListData) {
            return sessionInfo.getDcTypeCode(dcInfoListData.records);
        }
        return null
    }, [dcInfoListData, sessionInfo]);

    // ##### GraphQL Mutation. 'onError' for prevent from printing error at console (Uncaught exception) ###
    const [saveTransPlanReturns, responseSave] = useMutation( SAVE_TRANS_PLAN_RETURN, {
		onCompleted: (data, option) => onCompleteSaveReturns(data, option), 
		onError: (error) => addAlert({title: '물류집기 정보 저장 오류', message: [error.message]})
	} );
    const [setTransPlanNoReturn, responseSet] = useMutation( SET_TRANS_PLAN_RETURN_NO_RETURN, {
        onCompleted: (data, option) => onCompleteSetNoReturn(data, option),
        onError: (error) => addAlert({title: '반납정보 미입력 설정 오류', message: [error.message]})
    });
    const [setTransPlanDcCheck, responseDcCheck] = useMutation( SET_TRANS_PLAN_RETURN_DC_CHECK, {
        onCompleted: (data, option) => onCompleteSetDcCheck(data, option),
        onError: (error) => addAlert({title: '집기반납 확인 오류', message: [error.message]})
    });
    const [takeAtStoreTransPlanSealCheckInfo, responseTaking] = useMutation( TAKE_AT_STORE_TRANS_PLAN_SEAL_CHECK_INFO, {
        onCompleted: (data, option) => onCompleteTakingOver(data, option), 
        onError: (error) => addAlert({title: '점포인수 오류', message: [error.message]})
    } );
    //DurationPicker
    useEffect(()=>{
        if(open && item) {
            const inputSet = {...AppObject.emptyInputData};
            let editing = false;
            inputSet.data = {...item};
            if(!inputSet.data.inputArrTime) inputSet.data.inputArrTime = ValueMisc.getYMD() + AppWord.HMS_TAIL;
            if(!inputSet.data.inputDptrTime) {
                if(item.inputArrTime) inputSet.data.inputDptrTime = item.inputArrTime;
                else inputSet.data.inputDptrTime = ValueMisc.getYMD() + AppWord.HMS_TAIL;
            }
            editing = true;
                
            setInput(inputSet);
            setIsEdit(editing);
            setDcAndStore({dcName:item.dcName, storeName:item.storeName});
        }
    }, [open, item]);

    const onCompleteSaveReturns = (data, clientOption) => {
        if(data.transPlanReturnSave.ok) {
            if(data.transPlanReturnSave.detail !== ApiMsg.OK) {
                addAlert({
                    title: "물류집기 정보 저장",
                    message: [data.transPlanReturnSave.detail]
                });
            }
            else enqueueSnackbar("물류집기 정보가 저장되었습니다.", {variant:'success', autoHideDuration: 1500});
            // onCompleteMutation();

            // TSR 점포인수
            const param = {planId: item.planId};
            takeAtStoreTransPlanSealCheckInfo({variables: {transPlanSealCheckInfo: param}});
        }
        else addAlert({title:'물류집기 정보 저장 오류', message:[data.transPlanReturnSave.message]});
    };

    const onCompleteSetNoReturn = (data, clientOption) => {
        if(data.transPlanReturnSetNoReturn.ok) {
            enqueueSnackbar("반납정보 미입력 설정이 완료되었습니다.", {variant:'success', autoHideDuration: 1500});
            onCompleteMutation();
        }
        else addAlert({title:'반납정보 미입력 설정 오류', message:[data.transPlanReturnSetNoReturn.message]});
    };

    const onCompleteSetDcCheck = (data, clientOption) => {
        if(data.transPlanReturnSetDcCheck.ok) {
            enqueueSnackbar("집기반납 확인이 완료되었습니다.", {variant:'success', autoHideDuration: 1500});
            onCompleteMutation();
        }
        else addAlert({title:'집기반납 확인 오류', message:[data.transPlanReturnSetDcCheck.message]});
    };

    const onCompleteTakingOver = (data, clientOption) => {
        if(data.transPlanSealCheckInfoTakeAtStore.ok) {
            enqueueSnackbar('점포 인수가 완료되었습니다.', {variant: 'success', autoHideDuration: 1500});
        }
        else addAlert({title:'점포인수 오류', message:[data.transPlanSealCheckInfoTakeAtStore.message]});
        onCompleteMutation(); // anyway
    };

    const onChangeValue = (id, value, error) => {
        const newInput = InputUtil.reflectData(input, id, value, error, InputMap, isEdit);
        setInput(newInput);
    };

    // 저장 및 점포 TSR 인수확인
    const onClickSubmit = () => {
        const param = InputUtil.getDataToSubmit(input.data, InputMap, isEdit);
        saveTransPlanReturns({
            variables: {
                transPlanReturn: param,
            },
        });
    };

    const askCancel = async () => {
        const confirm = await openConfirmDialog({
            ...AppObject.messageCancelEdit,
            option: {yesLabel: AppWord.IgnoreAndClose}
        });
        if(confirm) onClose();
    };

    const onClickCancel = () => {
        if(input.changed) askCancel();
        else onClose();
    };

    const onChangeFrom = (from) => {
        onChangeValue(InputMap.inputArrTime.id, from, false);
    };

    const onChangeTill = (till) => {
        onChangeValue(InputMap.inputDptrTime.id, till, false);
    };

    const onClickGoToIssueTSR = () => {
        let menuSet;
        if(sessionInfo.isStoreUser()) {
            menuSet = {
                menu: {
                    menuId: AppMenuUrl.StoreTSRMenuId,
                    menuUrl: AppMenuUrl.StoreTSR,
                    svcUrl: AppMenuUrl.StoreTSR,
                    menuName: 'TSR 발행',
                    lastMenu: '1',
                    nodeType: 'leaf',
                },
                parentMenu: null,
            };
        }
        else {
            menuSet = ValueMisc.findMenu(AppMenuUrl.TSR, menuTree);
            if(!menuSet.menu) {
                addAlert({
                    title: 'TSR 발행 화면 이동',
                    message: ['TSR 발행 화면이 없습니다. 관리자에게 문의해 주세요.'],
                });
                return;
            }
        }
        transPlanTsrListParamRepo({
            dcCd: item.dcCd,
            storeId: item.storeId,
            planDate: item.planDate,
        });
        menuRequestRepo(menuSet);
    };

    // 집기반납 확인 버튼
    const onClickCheckReturning = async () => {
        const confirm = await openConfirmDialog({
            title: '집기반납 확인',
            content: [
                '물류 집기반납 이상 없음을 확인합니다.',
                '계속하시겠습니까?',
            ]
        });
        if(confirm) {
            setTransPlanDcCheck({variables: {transPlanReturn: {planId: item.planId}}});
        }
    };

    // 반납정보 미입력 버튼
    const onClickSaveNoReturnMemo = () => {
        setOpenNoReturnInput(true);
    };

    const onRequestSaveNoReturnData = (info, isEdit) => {
        // isEdit is not used
        setTransPlanNoReturn({variables: {transPlanReturn: info}});
    };

    if(!open || !item) return null;

    const canSave = checkAuth(authTableTree.tree.pr_trans_plan_return_save);
    const canCheck = checkAuth(authTableTree.tree.pr_trans_plan_return_dc_check);
    const canSet = checkAuth(authTableTree.tree.pr_trans_plan_return_set_no_return);

    const loadingAny
    = responseSave.loading
    || responseSet.loading
    || responseDcCheck.loading
    || responseTaking.loading;

    return(
        <EditorBox>
            <ContentHeader style={{borderBottom: AppStyle.BorderCCC}}>
                <ControlBox>
                    <span style={{marginLeft:10, fontWeight:'bold', color: AppStyle.PrimaryRGB}}>
                        {dcAndStore.dcName}[{item.dcCd}] ~ {dcAndStore.storeName}[{item.storeId}] {item.planDate}
                    </span>
                </ControlBox>
                <ToolBox>
                    <Button
                        onClick={onClickGoToIssueTSR}
                        variant={AppStyle.mui.contained}
                        color={AppStyle.mui.secondary}
                        startIcon={<ReceiptLongIcon/>}
                        disabled={loadingAny}
                    >
                        TSR 발행 이동
                    </Button>
                    <Button onClick={onClickSubmit}
                        variant={AppStyle.mui.contained}
                        color={AppStyle.mui.primary}
                        startIcon={<CloudUploadIcon/>}
                        style={{marginLeft:10}}
                        disabled={loadingAny || !canSave}
                    >
                        저장 및 점포 TSR 인수확인
                    </Button>
                    <Button onClick={onClickCheckReturning}
                        variant={AppStyle.mui.contained}
                        color={AppStyle.mui.inherit}
                        startIcon={<RepartitionIcon/>}
                        style={{marginLeft:10}}
                        disabled={
                            loadingAny || !canCheck || item.returnDcCheckYn==='Y'
                        }
                    >
                        집기반납 확인
                    </Button>
                    <Button onClick={onClickSaveNoReturnMemo}
                        variant={AppStyle.mui.contained}
                        color={AppStyle.mui.inherit}
                        startIcon={<AssignmentLateIcon/>}
                        style={{marginLeft:10}}
                        disabled={loadingAny || !canSet}
                    >
                        반납정보 미입력
                    </Button>
                    <Button onClick={onClickCancel}
                        variant={AppStyle.mui.contained}
                        color={AppStyle.mui.inherit}
                        startIcon={<ArrowBackIcon/>}
                        style={{marginLeft:10}}
                        disabled={loadingAny}
                    >닫기</Button>
                </ToolBox>
            </ContentHeader>
            <EditorBody>
                <Grid container spacing={2}>
                    <Grid item xs={1}>
                        <GroupLabel>도착과 출발</GroupLabel>
                    </Grid>
                    <Grid item xs={11}>
                        <InputGroupBox style={{paddingLeft:5}}>
                            <DurationPicker
                                from={input.data.inputArrTime || ValueMisc.getYMD() + AppWord.HMS_TAIL}
                                onChangeFrom={onChangeFrom}
                                till={input.data.inputDptrTime || ValueMisc.getYMD() + AppWord.HMS_TAIL}
                                onChangeTill={onChangeTill}
                                labelFrom='점포도착'
                                labelTill='점포출발'
                                tailFrom='시 도착'
                                tailTill='시 출발'
                            />
                        </InputGroupBox>
                    </Grid>
                    <Grid item xs={1}>
                        <GroupLabel>도착온도</GroupLabel>
                    </Grid>
                    <Grid item xs={11}>
                        <InputGroupBox>
                            <InputHolder style={{maxWidth:150}}>
                                <VzmTextField
                                    dict={InputMap.inputTempF}
                                    value={input.data.inputTempF}
                                    onChange={onChangeValue}
                                    fullWidth={true}
                                />
                            </InputHolder>
                            <InputHolder style={{maxWidth:150}}>
                                <VzmTextField
                                    dict={InputMap.inputTempM}
                                    value={input.data.inputTempM}
                                    onChange={onChangeValue}
                                    fullWidth={true}
                                />
                            </InputHolder>
                            <InputHolder style={{maxWidth:150}}>
                                <VzmTextField
                                    dict={InputMap.inputTempR}
                                    value={input.data.inputTempR}
                                    onChange={onChangeValue}
                                    fullWidth={true}
                                />
                            </InputHolder>
                        </InputGroupBox>
                    </Grid>
                    <Grid item xs={1}>
                        <GroupLabel>센터반납</GroupLabel>
                    </Grid>
                    <Grid item xs={11}>
                        <InputGroupBox>
                            <InputHolder>
                                <LabelOverInputBox>
                                    <InputLabelBox>Pallet</InputLabelBox>
                                    <PairInput>
                                        <PairLeft>
                                            <VzmIntField
                                                dict={InputMap.dcReturnKpp}
                                                value={input.data.dcReturnKpp}
                                                onChange={onChangeValue}
                                                fullWidth={true}
                                            />
                                        </PairLeft>
                                        <PairRight>
                                            <VzmIntField
                                                dict={InputMap.dcReturnAju}
                                                value={input.data.dcReturnAju}
                                                onChange={onChangeValue}
                                                fullWidth={true}
                                            />
                                        </PairRight>
                                    </PairInput>
                                </LabelOverInputBox>
                            </InputHolder>
                            <InputHolder>
                                <LabelOverInputBox>
                                    <InputLabelBox>집기적재 Pallet</InputLabelBox>
                                    <PairInput>
                                        <PairLeft>
                                            <VzmIntField
                                                dict={InputMap.dcReturnStackKpp}
                                                value={input.data.dcReturnStackKpp}
                                                onChange={onChangeValue}
                                                fullWidth={true}
                                            />
                                        </PairLeft>
                                        <PairRight>
                                            <VzmIntField
                                                dict={InputMap.dcReturnStackAju}
                                                value={input.data.dcReturnStackAju}
                                                onChange={onChangeValue}
                                                fullWidth={true}
                                            />
                                        </PairRight>
                                    </PairInput>
                                </LabelOverInputBox>
                            </InputHolder>
                            <InputHolder>
                                <LabelOverInputBox>
                                    <InputLabelBox>Single Pick Tray</InputLabelBox>
                                    <PairInput>
                                        <PairLeft>
                                            <VzmIntField
                                                dict={InputMap.dcReturnSpt}
                                                value={input.data.dcReturnSpt}
                                                onChange={onChangeValue}
                                                fullWidth={true}
                                            />
                                        </PairLeft>
                                        <PairRight>
                                            <VzmIntField
                                                dict={InputMap.dcReturnSpt533}
                                                value={input.data.dcReturnSpt533}
                                                onChange={onChangeValue}
                                                fullWidth={true}
                                            />
                                        </PairRight>
                                    </PairInput>
                                </LabelOverInputBox>
                            </InputHolder>
                            <InputHolder>
                                <LabelOverInputBox>
                                    <InputLabelBox>보냉커버</InputLabelBox>
                                    <PairInput>
                                        <PairLeft>
                                            <VzmIntField
                                                dict={InputMap.dcReturnFull}
                                                value={input.data.dcReturnFull}
                                                onChange={onChangeValue}
                                                fullWidth={true}
                                            />
                                        </PairLeft>
                                        <PairRight>
                                            <VzmIntField
                                                dict={InputMap.dcReturnHalf}
                                                value={input.data.dcReturnHalf}
                                                onChange={onChangeValue}
                                                fullWidth={true}
                                            />
                                        </PairRight>
                                    </PairInput>
                                </LabelOverInputBox>
                            </InputHolder>
                            <InputHolder>
                                <LabelOverInputBox>
                                    <InputLabelBox>NJC800</InputLabelBox>
                                    <SingleInput>
                                        <VzmIntField
                                            dict={InputMap.dcReturnNjc800}
                                            value={input.data.dcReturnNjc800}
                                            onChange={onChangeValue}
                                            fullWidth={true}
                                        />
                                    </SingleInput>
                                </LabelOverInputBox>
                            </InputHolder>
                            <InputHolder>
                                <LabelOverInputBox>
                                    <InputLabelBox>
                                    {
                                        dcTypeCd === 'A' ? '양곡 단프라' : 'Roll Cage'
                                    }
                                    </InputLabelBox>
                                    <SingleInput>
                                        <VzmIntField
                                            dict={InputMap.dcReturnRollcage}
                                            value={input.data.dcReturnRollcage}
                                            onChange={onChangeValue}
                                            fullWidth={true}
                                            label={dcTypeCd === 'A' ? '양곡 단프라' : 'Roll Cage'}
                                        />
                                    </SingleInput>
                                </LabelOverInputBox>
                            </InputHolder>
                            <InputHolder>
                                <LabelOverInputBox>
                                    <InputLabelBox>
                                    {
                                        dcTypeCd === 'A' ? '고가 단프라' : 'Danpra'
                                    }
                                    </InputLabelBox>
                                    <SingleInput>
                                        <VzmIntField
                                            dict={InputMap.dcReturnDanpra}
                                            value={input.data.dcReturnDanpra}
                                            onChange={onChangeValue}
                                            fullWidth={true}
                                            label={dcTypeCd === 'A' ? '고가 단프라' : 'Danpra'}
                                        />
                                    </SingleInput>
                                </LabelOverInputBox>
                            </InputHolder>
                            <InputHolder>
                                <LabelOverInputBox>
                                    <InputLabelBox>우든칼라</InputLabelBox>
                                    <SingleInput>
                                        <VzmIntField
                                            dict={InputMap.dcReturnWooden}
                                            value={input.data.dcReturnWooden}
                                            onChange={onChangeValue}
                                            fullWidth={true}
                                        />
                                    </SingleInput>
                                </LabelOverInputBox>
                            </InputHolder>
                            <InputHolder>
                                <LabelOverInputBox>
                                    <InputLabelBox>옥타곤</InputLabelBox>
                                    <SingleInput>
                                        <VzmIntField
                                            dict={InputMap.dcReturnOctagon}
                                            value={input.data.dcReturnOctagon}
                                            onChange={onChangeValue}
                                            fullWidth={true}
                                        />
                                    </SingleInput>
                                </LabelOverInputBox>
                            </InputHolder>
                            <InputHolder>
                                <LabelOverInputBox>
                                    <InputLabelBox>기타</InputLabelBox>
                                    <SingleInput>
                                        <VzmIntField
                                            dict={InputMap.dcReturnEtc}
                                            value={input.data.dcReturnEtc}
                                            onChange={onChangeValue}
                                            fullWidth={true}
                                        />

                                    </SingleInput>
                                </LabelOverInputBox>
                            </InputHolder>
                        </InputGroupBox>
                    </Grid>
                    <Grid item xs={1}>
                        <GroupLabel>점간대출(RTV)</GroupLabel>
                    </Grid>
                    <Grid item xs={11}>
                        <InputGroupBox>
                            <InputHolder style={{maxWidth:200}}>
                                <VzmTextField
                                    dict={InputMap.dcCoverStores}
                                    value={input.data.dcCoverStores}
                                    onChange={onChangeValue}
                                    fullWidth={true}
                                />
                            </InputHolder>
                            <InputHolder style={{maxWidth:150, marginLeft:30}}>
                                <VzmTextField
                                    dict={InputMap.dcReturnSealNo}
                                    value={input.data.dcReturnSealNo}
                                    onChange={onChangeValue}
                                    fullWidth={true}
                                />
                            </InputHolder>
                            <InputHolder style={{maxWidth:150}}>
                                <VzmTextField
                                    dict={InputMap.dcReturnSealNo2}
                                    value={input.data.dcReturnSealNo2}
                                    onChange={onChangeValue}
                                    fullWidth={true}
                                />
                            </InputHolder>
                            <InputHolder style={{maxWidth:150, marginRight:30}}>
                                <VzmTextField
                                    dict={InputMap.dcReturnSealNo3}
                                    value={input.data.dcReturnSealNo3}
                                    onChange={onChangeValue}
                                    fullWidth={true}
                                />
                            </InputHolder>
                            <InputHolder style={{maxWidth:150}}>
                                <VzmTextField
                                    dict={InputMap.dcCoverItem1}
                                    value={input.data.dcCoverItem1}
                                    onChange={onChangeValue}
                                    fullWidth={true}
                                />
                            </InputHolder>
                            <InputHolder style={{maxWidth:150}}>
                                <VzmTextField
                                    dict={InputMap.dcCoverItem2}
                                    value={input.data.dcCoverItem2}
                                    onChange={onChangeValue}
                                    fullWidth={true}
                                />
                            </InputHolder>
                        </InputGroupBox>
                    </Grid>
                    <Grid item xs={1}>
                        <GroupLabel>기타</GroupLabel>
                    </Grid>
                    <Grid item xs={11}>
                        <InputGroupBox>
                            <InputHolder style={{width:'100%'}}>
                                <VzmTextField
                                    dict={InputMap.dcReturnNote}
                                    value={input.data.dcReturnNote}
                                    onChange={onChangeValue}
                                    fullWidth={true}
                                />
                            </InputHolder>
                        </InputGroupBox>
                    </Grid>
                </Grid>
            </EditorBody>
            <TransPlanReturnNoReturnInput
                open={openNoReturnInput}
                item={item}
                onClose={() => setOpenNoReturnInput(false)}
                onRequestSave={onRequestSaveNoReturnData}
                responseSaving={responseSet}
            />
        </EditorBox>
    );
}

